import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Search, Users, Briefcase, Calendar, ChevronDown, ChevronUp, Globe,
  Languages, BookOpen, FileText, CheckCircle, Mail, Target, Clock, Video,
  Sparkles, Plus, Trash2, Send, RotateCcw, AlertCircle, Linkedin,
} from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'

// ── Types ─────────────────────────────────────────────────────────────────────

interface Client {
  user_id: number
  full_name: string | null
  email: string | null
  target_role: string | null
  total_sessions: number
  last_session_at: string | null
  timezone: string | null
  language: string | null
  linkedin_url: string | null
  experience_level: string | null
  interview_goals: string | null
  industry: string | null
}

interface QuestionItem {
  text: string
  category: string
  difficulty: string
  source: string
}

interface QuestionSet {
  id: number
  session_id: number
  questions: QuestionItem[]
  generation_context: { target_role?: string; experience_level?: string } | null
  is_finalized: boolean
  created_at: string
}

interface SessionItem {
  id: number
  title: string
  status: string
  scheduled_at: string
  duration_minutes: number
  interviewer_alias: string | null
  zoom_join_url: string | null
}

const CATEGORY_COLORS: Record<string, { bg: string; color: string }> = {
  behavioral:     { bg: 'rgba(59,130,246,0.1)',  color: '#2563eb' },
  technical:      { bg: 'rgba(16,185,129,0.1)',  color: '#059669' },
  leadership:     { bg: 'rgba(139,92,246,0.1)',  color: '#7c3aed' },
  problem_solving:{ bg: 'rgba(245,158,11,0.1)',  color: '#d97706' },
  cultural:       { bg: 'rgba(236,72,153,0.1)',  color: '#db2777' },
}

const DIFFICULTY_COLORS: Record<string, string> = {
  easy: '#16a34a', medium: '#d97706', hard: '#dc2626',
}

function formatShortDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateTime(iso: string) {
  const d = new Date(iso)
  return {
    date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function InfoRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-dark)' }}>{children}</span>
    </div>
  )
}

function SectionCard({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px', borderBottom: '1px solid var(--border)', background: '#fafbfc' }}>
        <span style={{ color: 'var(--primary-navy)' }}>{icon}</span>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-dark)' }}>{title}</h3>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  )
}

// ── Client Profile Panel ───────────────────────────────────────────────────────

function ClientProfile({ client, onClose }: { client: Client; onClose: () => void }) {
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([])
  const [loadingQS, setLoadingQS] = useState(false)
  const [sessions, setSessions] = useState<SessionItem[]>([])
  const [loadingSessions, setLoadingSessions] = useState(false)
  const [expandedSession, setExpandedSession] = useState<number | null>(null)

  // Enhanced profile data (job description, etc.)
  const [jobDescriptionText, setJobDescriptionText] = useState<string | null>(null)
  const [jobDescriptionPath, setJobDescriptionPath] = useState<string | null>(null)
  const [jdDownloadUrl, setJdDownloadUrl] = useState<string | null>(null)
  const [jdDownloading, setJdDownloading] = useState(false)
  const [specialNotes, setSpecialNotes] = useState<string | null>(null)

  // AI generation
  const [generatedQuestions, setGeneratedQuestions] = useState<QuestionItem[]>([])
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState('')

  // Final question set builder
  const [finalQuestions, setFinalQuestions] = useState<QuestionItem[]>([])
  const [customQuestion, setCustomQuestion] = useState('')
  const [savingQS, setSavingQS] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')

  const displayName = client.full_name || client.email || 'Client'

  // Load question sets, sessions, and enhanced profile on mount
  useEffect(() => {
    setLoadingQS(true)
    interviewerApi.getClientQuestionSets(client.user_id)
      .then((data) => setQuestionSets(Array.isArray(data) ? data : []))
      .catch(() => setQuestionSets([]))
      .finally(() => setLoadingQS(false))

    setLoadingSessions(true)
    interviewerApi.getClientSessions(client.user_id)
      .then((data) => setSessions(Array.isArray(data) ? data : []))
      .catch(() => setSessions([]))
      .finally(() => setLoadingSessions(false))

    // Load job description and special notes from most recent booking
    interviewerApi.getClientPrepInfo(client.user_id)
      .then((data) => {
        setJobDescriptionText(data?.job_description_text ?? null)
        setJobDescriptionPath(data?.job_description_path ?? null)
        setJdDownloadUrl(null)
        setSpecialNotes(data?.special_notes ?? null)
      })
      .catch(() => {
        setJobDescriptionText(null)
        setJobDescriptionPath(null)
        setSpecialNotes(null)
      })
  }, [client.user_id])

  const handleGenerate = async () => {
    setGenerating(true)
    setGenError('')
    setGeneratedQuestions([])
    try {
      const questions = await interviewerApi.generateQuestionsForClient(client.user_id, {
        num_questions: 8,
        job_description: null,
      })
      setGeneratedQuestions(questions || [])
    } catch {
      setGenError('Failed to generate questions. Please try again.')
    } finally {
      setGenerating(false)
    }
  }

  const addToFinal = (q: QuestionItem) => {
    if (!finalQuestions.find((fq) => fq.text === q.text)) {
      setFinalQuestions((prev) => [...prev, q])
    }
  }

  const addCustomQuestion = () => {
    const text = customQuestion.trim()
    if (!text) return
    setFinalQuestions((prev) => [...prev, { text, category: 'behavioral', difficulty: 'medium', source: 'manual' }])
    setCustomQuestion('')
  }

  const removeFromFinal = (idx: number) => {
    setFinalQuestions((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleSaveFinal = async () => {
    if (finalQuestions.length === 0 || sessions.length === 0) return
    setSavingQS(true)
    setSaveMsg('')
    try {
      const latestSession = sessions[0]
      await interviewerApi.saveQuestionSet({
        session_id: latestSession.id,
        questions: finalQuestions,
        is_finalized: true,
      })
      setSaveMsg('Question set saved successfully!')
      setTimeout(() => setSaveMsg(''), 3000)
    } catch {
      setSaveMsg('Failed to save question set.')
      setSavingQS(false)
      return
    }
    setSavingQS(false)
    // Reload question sets separately — failure here doesn't affect the save success message
    interviewerApi.getClientQuestionSets(client.user_id)
      .then((data) => setQuestionSets(Array.isArray(data) ? data : []))
      .catch(() => {/* silently ignore reload failure */})
  }

  return (
    <div>
      {/* Client header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, fontWeight: 700, flexShrink: 0 }}>
          {displayName.charAt(0).toUpperCase()}
        </div>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-dark)', marginBottom: 2 }}>{displayName}</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{client.target_role || 'No target role set'}</p>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--primary-navy)' }}>
              {loadingSessions ? '—' : sessions.filter((s) => s.status === 'completed').length}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Completed Sessions</div>
          </div>
          {client.linkedin_url && (
            <a href={client.linkedin_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" style={{ padding: '8px 14px', fontSize: 13 }}>
              <Linkedin size={14} /> LinkedIn
            </a>
          )}
          <button className="btn btn-outline" style={{ padding: '8px 14px', fontSize: 13 }} onClick={onClose}>
            ← Back
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* ── Left column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Client Information */}
          <SectionCard title="Client Information" icon={<Users size={16} />}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <InfoRow label="Email">
                {client.email
                  ? <a href={`mailto:${client.email}`} style={{ color: 'var(--primary-navy)', display: 'flex', alignItems: 'center', gap: 4 }}><Mail size={13} />{client.email}</a>
                  : '—'}
              </InfoRow>
              <InfoRow label="Target Role">
                {client.target_role
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Target size={13} />{client.target_role}</span>
                  : '—'}
              </InfoRow>
              <InfoRow label="Completed Sessions">
                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <CheckCircle size={13} style={{ color: '#16a34a' }} />
                  {loadingSessions ? '—' : sessions.filter((s) => s.status === 'completed').length}
                </span>
              </InfoRow>
              <InfoRow label="Last Session">
                {client.last_session_at
                  ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Calendar size={13} />{formatShortDate(client.last_session_at)}</span>
                  : '—'}
              </InfoRow>
              <InfoRow label="Timezone">
                {client.timezone ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Globe size={13} />{client.timezone}</span> : '—'}
              </InfoRow>
              <InfoRow label="Language">
                {client.language ? <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Languages size={13} />{client.language}</span> : '—'}
              </InfoRow>
            </div>
          </SectionCard>

          {/* Job Description */}
          <SectionCard title="Job Description" icon={<FileText size={16} />}>
            {/* PDF download button */}
            {jobDescriptionPath && (
              <div style={{ marginBottom: 12 }}>
                {jdDownloadUrl ? (
                  <a
                    href={jdDownloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                    style={{ fontSize: 13, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                  >
                    <FileText size={14} /> Download JD (PDF)
                  </a>
                ) : (
                  <button
                    className="btn btn-outline"
                    style={{ fontSize: 13, padding: '6px 14px', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    disabled={jdDownloading}
                    onClick={async () => {
                      setJdDownloading(true)
                      try {
                        const res = await interviewerApi.getJdSignedUrl(jobDescriptionPath)
                        setJdDownloadUrl(res.signed_url)
                        window.open(res.signed_url, '_blank')
                      } catch {
                        alert('Failed to generate download link. Please try again.')
                      } finally {
                        setJdDownloading(false)
                      }
                    }}
                  >
                    <FileText size={14} />
                    {jdDownloading ? 'Generating link...' : 'View JD (PDF)'}
                  </button>
                )}
              </div>
            )}
            {jobDescriptionText ? (
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--text-dark)', lineHeight: 1.7, maxHeight: 260, overflowY: 'auto', padding: '10px 14px', background: '#f8f9fb', borderRadius: 8, border: '1px solid var(--border)' }}>
                {jobDescriptionText}
              </div>
            ) : !jobDescriptionPath ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={14} />
                No job description provided. The client can share one when booking a session.
              </p>
            ) : null}
          </SectionCard>

          {/* Suggested Question Templates */}
          <SectionCard title="Suggested Question Templates" icon={<BookOpen size={16} />}>
            {loadingQS ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
            ) : questionSets.length > 0 ? (
              <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 4 }}>
                  {questionSets[0].generation_context?.target_role ?? client.target_role ?? 'General'}
                  {(questionSets[0].generation_context?.experience_level ?? client.experience_level)
                    ? ` – ${questionSets[0].generation_context?.experience_level ?? client.experience_level}`
                    : ''}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  {client.industry || 'Technology'}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  <BookOpen size={12} style={{ display: 'inline', marginRight: 4 }} />
                  {questionSets[0].questions.length} questions
                </div>
                <button
                  className="btn btn-outline"
                  style={{ marginTop: 12, padding: '6px 14px', fontSize: 13 }}
                  onClick={() => setFinalQuestions(questionSets[0].questions)}
                >
                  Use This Set
                </button>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No question templates yet. Generate questions below.
              </p>
            )}
          </SectionCard>
        </div>

        {/* ── Right column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* Interview Goals & Notes */}
          <SectionCard title="Interview Goals & Notes" icon={<Target size={16} />}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {client.experience_level && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Experience Level</div>
                  <span className="badge badge-primary" style={{ fontSize: 13 }}>{client.experience_level}</span>
                </div>
              )}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Interview Goals</div>
                <p style={{ fontSize: 14, color: client.interview_goals ? 'var(--text-dark)' : 'var(--text-muted)', fontStyle: client.interview_goals ? 'normal' : 'italic', lineHeight: 1.6 }}>
                  {client.interview_goals || 'No interview goals provided'}
                </p>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Special Notes or Circumstances</div>
                <p style={{ fontSize: 14, color: specialNotes ? 'var(--text-dark)' : 'var(--text-muted)', fontStyle: specialNotes ? 'normal' : 'italic', lineHeight: 1.6 }}>
                  {specialNotes || 'No special notes provided'}
                </p>
              </div>
            </div>
          </SectionCard>

          {/* AI-Generated Questions */}
          <SectionCard title="AI-Generated Questions" icon={<Sparkles size={16} />}>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
              Generate personalized interview questions based on the client's profile, goals, and target role.
            </p>

            <button
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={generating}
              style={{ marginBottom: 16 }}
            >
              {generating ? (
                <><RotateCcw size={14} style={{ animation: 'spin 1s linear infinite' }} /> Generating…</>
              ) : (
                <><Sparkles size={14} /> Generate Questions</>
              )}
            </button>

            {genError && (
              <div style={{ display: 'flex', gap: 8, padding: '10px 14px', background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.2)', borderRadius: 8, fontSize: 13, color: '#dc2626', marginBottom: 12 }}>
                <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {genError}
              </div>
            )}

            {generatedQuestions.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {generatedQuestions.map((q, i) => {
                  const catStyle = CATEGORY_COLORS[q.category] ?? CATEGORY_COLORS.behavioral
                  const inFinal = finalQuestions.some((fq) => fq.text === q.text)
                  return (
                    <div key={i} style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'white', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 14, color: 'var(--text-dark)', lineHeight: 1.5, marginBottom: 8 }}>{q.text}</p>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: catStyle.bg, color: catStyle.color }}>
                            {q.category.replace('_', ' ')}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: `${DIFFICULTY_COLORS[q.difficulty]}18`, color: DIFFICULTY_COLORS[q.difficulty] }}>
                            {q.difficulty}
                          </span>
                        </div>
                      </div>
                      <button
                        className="btn btn-outline"
                        style={{ padding: '5px 10px', fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0, ...(inFinal ? { opacity: 0.4 } : {}) }}
                        onClick={() => addToFinal(q)}
                        disabled={inFinal}
                        title={inFinal ? 'Already in Final Set' : 'Add to Final Set'}
                      >
                        <Plus size={13} /> Add
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ── Final Question Set (full-width) ── */}
      <div style={{ marginTop: 20 }}>
        <SectionCard title="Final Question Set" icon={<Send size={16} />}>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
            Build your final question set by adding AI-generated questions above or typing your own.
            {sessions.length > 0 ? ` It will be saved to the session on ${formatDateTime(sessions[0].scheduled_at).date}.` : ''}
          </p>

          {/* Custom question input */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            <input
              type="text"
              className="form-input"
              placeholder="Type a custom question and press Add…"
              value={customQuestion}
              onChange={(e) => setCustomQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && addCustomQuestion()}
              style={{ flex: 1 }}
            />
            <button className="btn btn-outline" onClick={addCustomQuestion} disabled={!customQuestion.trim()}>
              <Plus size={14} /> Add
            </button>
          </div>

          {finalQuestions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-muted)' }}>
              <BookOpen size={36} style={{ marginBottom: 10, opacity: 0.4 }} />
              <p style={{ fontSize: 13 }}>No questions added yet. Use the + button on AI-generated questions above, or type your own.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {finalQuestions.map((q, i) => {
                const catStyle = CATEGORY_COLORS[q.category] ?? CATEGORY_COLORS.behavioral
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, background: '#fafbfc' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', minWidth: 22, paddingTop: 1 }}>{i + 1}.</span>
                    <p style={{ flex: 1, fontSize: 14, color: 'var(--text-dark)', lineHeight: 1.5, margin: 0 }}>{q.text}</p>
                    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: catStyle.bg, color: catStyle.color, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {q.category.replace('_', ' ')}
                    </span>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', flexShrink: 0, padding: '2px 4px' }} onClick={() => removeFromFinal(i)} title="Remove">
                      <Trash2 size={15} />
                    </button>
                  </div>
                )
              })}
            </div>
          )}

          {saveMsg && (
            <div style={{ padding: '10px 14px', borderRadius: 8, fontSize: 13, marginBottom: 12, background: saveMsg.includes('Failed') ? 'rgba(220,38,38,0.06)' : 'rgba(22,163,74,0.06)', color: saveMsg.includes('Failed') ? '#dc2626' : '#16a34a', border: `1px solid ${saveMsg.includes('Failed') ? 'rgba(220,38,38,0.2)' : 'rgba(22,163,74,0.2)'}` }}>
              {saveMsg}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {finalQuestions.length > 0 && (
              <button className="btn btn-outline" onClick={() => setFinalQuestions([])}>
                <Trash2 size={14} /> Clear All
              </button>
            )}
            <button
              className="btn btn-primary"
              onClick={handleSaveFinal}
              disabled={savingQS || finalQuestions.length === 0 || sessions.length === 0}
              title={sessions.length === 0 ? 'No sessions found for this client' : ''}
            >
              <Send size={14} />
              {savingQS ? 'Saving…' : `Save Question Set (${finalQuestions.length})`}
            </button>
          </div>
        </SectionCard>
      </div>

      {/* ── Previously Saved Question Sets ── */}
      {(loadingQS || questionSets.length > 0) && (
        <div style={{ marginTop: 20 }}>
          <SectionCard title={`Previously Saved Question Sets${questionSets.length > 0 ? ` (${questionSets.length})` : ''}`} icon={<BookOpen size={16} />}>
            {loadingQS ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {questionSets.map((qs) => {
                  const label = qs.generation_context?.target_role ?? client.target_role ?? 'General Interview'
                  const expLevel = qs.generation_context?.experience_level ?? client.experience_level
                  return (
                    <div
                      key={qs.id}
                      style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => setFinalQuestions(qs.questions)}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>
                          {label}{expLevel ? ` – ${expLevel}` : ''}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 12 }}>
                          <span>{formatShortDate(qs.created_at)}</span>
                          <span>{qs.questions.length} questions</span>
                        </div>
                      </div>
                      {qs.is_finalized && (
                        <span className="badge badge-success" style={{ fontSize: 11 }}>Finalized</span>
                      )}
                      <span style={{ fontSize: 12, color: 'var(--primary-navy)', fontWeight: 500 }}>Load →</span>
                    </div>
                  )
                })}
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* ── Completed Sessions ── */}
      {(() => {
        const completed = sessions.filter((s) => s.status === 'completed')
        return (
          <div style={{ marginTop: 20 }}>
            <SectionCard title={`Completed Sessions${completed.length > 0 ? ` (${completed.length})` : ''}`} icon={<CheckCircle size={16} />}>
              {loadingSessions ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</p>
              ) : completed.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>
                  <CheckCircle size={36} style={{ marginBottom: 10, opacity: 0.4 }} />
                  <p style={{ fontSize: 13 }}>No completed sessions yet.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {completed.map((session) => {
                    const { date, time } = formatDateTime(session.scheduled_at)
                    const isExpanded = expandedSession === session.id
                    return (
                      <div key={session.id} style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden', marginBottom: 8 }}>
                        <div
                          style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', cursor: 'pointer', background: isExpanded ? '#f8f9fa' : 'white' }}
                          onClick={() => setExpandedSession(isExpanded ? null : session.id)}
                        >
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(45,55,72,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Video size={16} style={{ color: 'var(--primary-navy)' }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>{session.title}</div>
                            <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <Calendar size={12} />{date} · <Clock size={12} />{time}
                            </div>
                          </div>
                          <span className="badge badge-success" style={{ fontSize: 12 }}>completed</span>
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </div>
                        {isExpanded && (
                          <div style={{ borderTop: '1px solid var(--border)', padding: '16px', background: '#fafbfc', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
                            <InfoRow label="Duration"><Clock size={12} style={{ display: 'inline', marginRight: 4 }} />{session.duration_minutes} min</InfoRow>
                            <InfoRow label="Target Role">{client.target_role || '—'}</InfoRow>
                            <InfoRow label="Interviewer">{session.interviewer_alias || '—'}</InfoRow>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </SectionCard>
          </div>
        )
      })()}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function InterviewerClients() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [clients, setClients] = useState<Client[]>([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Which client is open in full-profile view (from ?client= param)
  const clientIdParam = searchParams.get('client')
  const selectedClientId = clientIdParam && clientIdParam !== 'null' ? parseInt(clientIdParam) : null

  useEffect(() => {
    interviewerApi
      .getClients()
      .then((data) => setClients(data || []))
      .catch(() => setError('Failed to load clients.'))
      .finally(() => setLoading(false))
  }, [])

  const selectedClient = selectedClientId
    ? clients.find((c) => c.user_id === selectedClientId) ?? null
    : null

  const clearClient = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('client')
    next.delete('prep')
    setSearchParams(next)
  }

  const openClient = (userId: number) => {
    setSearchParams({ prep: '1', client: String(userId) })
  }

  const filteredClients = clients.filter((c) => {
    const q = search.toLowerCase()
    return (
      (c.full_name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.target_role || '').toLowerCase().includes(q)
    )
  })

  if (loading) return <div className="loading-screen">Loading clients...</div>

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Client Profiles</h1>
        <p className="page-subtitle">View client details and session history</p>
      </div>

      {/* Full-profile view when client is selected */}
      {selectedClient ? (
        <div className="card" style={{ padding: 24 }}>
          <ClientProfile client={selectedClient} onClose={clearClient} />
        </div>
      ) : (
        /* Client list */
        <div className="card">
          {/* Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 20, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
            <Search size={18} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search clients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ border: 'none', boxShadow: 'none', padding: '4px 0' }}
            />
          </div>

          {error && (
            <div className="empty-state"><Users size={48} /><h3>{error}</h3></div>
          )}

          {!error && filteredClients.length === 0 && (
            <div className="empty-state">
              <Users size={48} />
              <h3>No clients found</h3>
              <p>Clients will appear here once they book an interview session with you.</p>
            </div>
          )}

          {!error && filteredClients.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {filteredClients.map((client) => {
                const displayName = client.full_name || client.email || 'Unknown Client'
                return (
                  <div
                    key={client.user_id}
                    style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onClick={() => openClient(client.user_id)}
                  >
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'var(--primary-navy)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, fontWeight: 700, flexShrink: 0 }}>
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 3 }}>{displayName}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Briefcase size={13} />
                        {client.target_role || 'No target role set'}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-dark)' }}>{client.total_sessions}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sessions</div>
                    </div>
                    {client.last_session_at && (
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-dark)' }}>
                          {formatShortDate(client.last_session_at)}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Last Session</div>
                      </div>
                    )}
                    <ChevronDown size={18} style={{ color: 'var(--text-muted)', transform: 'rotate(-90deg)', flexShrink: 0 }} />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
